import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { calculateOptimalTimes } from '../utils';
import type {
  ActiveSection,
  CalculatorState,
  ScenarioType,
  SleepResult,
  UIState,
  TimeWheelState,
  UserProfile,
  MoonPhase,
  SleepType,
  SleepMode,
  UserSubscription,
  SubscriptionPlan,
  SubscriptionStatus,
  PaymentResult
} from '../types';

// Calculator store
interface CalculatorStore extends CalculatorState {
  result: SleepResult | null;
  isCalculating: boolean;
  
  // Actions
  setMode: (mode: SleepMode) => void;
  setTargetTime: (time: string) => void;
  setSleepType: (type: SleepType) => void;
  setFallAsleepTime: (minutes: number) => void;
  setWakeUpBuffer: (minutes: number) => void;
  setCustomCycleDuration: (minutes: number) => void;
  setNapDuration: (minutes: number) => void;
  setSleepEfficiency: (efficiency: number) => void;
  setCircadianType: (type: 'early' | 'normal' | 'late') => void;
  setWeekdayVsWeekend: (enabled: boolean) => void;
  calculateSleep: () => Promise<void>;
  clearResult: () => void;
  reset: () => void;
}

// UI store
interface UIStore extends UIState {
  // Actions
  setActiveSection: (section: ActiveSection) => void;
  toggleMobileMenu: () => void;
  setTheme: (theme: 'light' | 'dark' | 'auto') => void;
  addNotification: (notification: Omit<UIState['notifications'][0], 'id'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
}

// Scenarios store
interface ScenariosStore {
  selectedScenario: ScenarioType | null;
  customScenarios: any[];
  
  // Actions
  selectScenario: (scenario: ScenarioType) => void;
  applyScenarioToCalculator: (scenario: ScenarioType) => void;
  clearScenario: () => void;
}

// Moon Phase / Time Wheel store
interface TimeWheelStore extends TimeWheelState {
  // Actions
  updateCurrentTime: () => void;
  setHourFormat: (format: 12 | 24) => void;
  toggleLabels: () => void;
  updateMoonPhase: () => void;
}

// User profile store
interface UserStore {
  profile: UserProfile | null;
  isLoggedIn: boolean;
  subscription: UserSubscription | null;
  isProUser: boolean;
  isPremiumUser: boolean;
  
  // Actions
  setProfile: (profile: UserProfile) => void;
  updateProfile: (updates: Partial<UserProfile>) => void;
  clearProfile: () => void;
  login: () => void;
  logout: () => void;
  
  // Subscription actions
  setSubscription: (subscription: UserSubscription) => void;
  updateSubscription: (updates: Partial<UserSubscription>) => void;
  cancelSubscription: () => void;
  hasProAccess: () => boolean;
  hasPremiumAccess: () => boolean;
  getSubscriptionStatus: () => SubscriptionStatus | null;
  isSubscriptionActive: () => boolean;
  isFeatureAvailable: (featureTier: 'pro' | 'premium') => boolean;
}

// Sleep calculation utilities
const calculateSleepCycles = (targetTime: string, mode: SleepMode, sleepType: SleepType, fallAsleepTime: number): SleepResult => {
  const cycleDurations = {
    fast: 75,    // 1 hour 15 minutes
    normal: 90,  // 1 hour 30 minutes  
    slow: 105    // 1 hour 45 minutes
  };
  
  const cycleDuration = cycleDurations[sleepType];
  const [hours, minutes] = targetTime.split(':').map(Number);
  const targetMinutes = hours * 60 + minutes;
  
  const recommendedTimes = [];
  
  if (mode === 'bedtime') {
    // Calculate when to go to bed for optimal wake time
    for (let cycles = 4; cycles <= 6; cycles++) {
      const sleepDuration = cycles * cycleDuration;
      const bedtimeMinutes = targetMinutes - sleepDuration - fallAsleepTime;
      const adjustedBedtime = bedtimeMinutes < 0 ? bedtimeMinutes + 24 * 60 : bedtimeMinutes;
      
      recommendedTimes.push({
        hours: Math.floor(adjustedBedtime / 60) % 24,
        minutes: adjustedBedtime % 60
      });
    }
  } else if (mode === 'waketime') {
    // Calculate when to wake up for optimal sleep
    for (let cycles = 4; cycles <= 6; cycles++) {
      const sleepDuration = cycles * cycleDuration;
      const wakeTimeMinutes = targetMinutes + sleepDuration + fallAsleepTime;
      const adjustedWakeTime = wakeTimeMinutes >= 24 * 60 ? wakeTimeMinutes - 24 * 60 : wakeTimeMinutes;
      
      recommendedTimes.push({
        hours: Math.floor(adjustedWakeTime / 60),
        minutes: adjustedWakeTime % 60
      });
    }
  } else {
    // Nap mode - calculate optimal nap durations
    const napCycles = [1, 2]; // 1 or 2 cycles for naps
    for (const cycles of napCycles) {
      const napDuration = cycles * cycleDuration;
      const wakeTimeMinutes = targetMinutes + napDuration;
      const adjustedWakeTime = wakeTimeMinutes >= 24 * 60 ? wakeTimeMinutes - 24 * 60 : wakeTimeMinutes;
      
      recommendedTimes.push({
        hours: Math.floor(adjustedWakeTime / 60),
        minutes: adjustedWakeTime % 60
      });
    }
  }
  
  const selectedTime = recommendedTimes[Math.floor(recommendedTimes.length / 2)];
  const cycleCount = mode === 'nap' ? 1 : 5;
  const totalSleepDuration = cycleCount * cycleDuration;
  
  // Determine sleep quality based on duration and cycles
  let sleepQuality: 'excellent' | 'good' | 'fair' | 'poor';
  if (totalSleepDuration >= 450 && totalSleepDuration <= 540) { // 7.5-9 hours
    sleepQuality = 'excellent';
  } else if (totalSleepDuration >= 360 && totalSleepDuration < 450) { // 6-7.5 hours
    sleepQuality = 'good';
  } else if (totalSleepDuration >= 270 && totalSleepDuration < 360) { // 4.5-6 hours
    sleepQuality = 'fair';
  } else {
    sleepQuality = 'poor';
  }
  
  const tips = [
    'Try to maintain a consistent sleep schedule',
    'Avoid caffeine 6 hours before bedtime',
    'Keep your bedroom cool and dark',
    'Use blue light filters 2 hours before sleep'
  ];
  
  return {
    recommendedTimes,
    selectedTime,
    cycleCount,
    totalSleepDuration,
    sleepQuality,
    tips
  };
};

// Moon phase calculation
const calculateMoonPhase = (date: Date): MoonPhase => {
  // Simplified moon phase calculation
  const newMoon = new Date('2024-01-01'); // Known new moon date
  const daysSinceNewMoon = Math.floor((date.getTime() - newMoon.getTime()) / (1000 * 60 * 60 * 24));
  const cycle = daysSinceNewMoon % 29.53; // Lunar cycle is approximately 29.53 days
  
  let phase: MoonPhase['phase'];
  let illumination: number;
  let name: string;
  
  if (cycle < 1) {
    phase = 'new';
    name = 'New Moon';
    illumination = 0;
  } else if (cycle < 7.38) {
    phase = 'waxing_crescent';
    name = 'Waxing Crescent';
    illumination = cycle / 14.76;
  } else if (cycle < 8.84) {
    phase = 'first_quarter';
    name = 'First Quarter';
    illumination = 0.5;
  } else if (cycle < 14.76) {
    phase = 'waxing_gibbous';
    name = 'Waxing Gibbous';
    illumination = 0.5 + (cycle - 8.84) / 11.84;
  } else if (cycle < 16.22) {
    phase = 'full';
    name = 'Full Moon';
    illumination = 1;
  } else if (cycle < 22.61) {
    phase = 'waning_gibbous';
    name = 'Waning Gibbous';
    illumination = 1 - (cycle - 16.22) / 12.78;
  } else if (cycle < 24.07) {
    phase = 'last_quarter';
    name = 'Last Quarter';
    illumination = 0.5;
  } else {
    phase = 'waning_crescent';
    name = 'Waning Crescent';
    illumination = 0.5 - (cycle - 24.07) / 11.02;
  }
  
  return {
    name,
    illumination: Math.max(0, Math.min(1, illumination)),
    phase,
    date
  };
};

// Create stores
export const useCalculatorStore = create<CalculatorStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        mode: 'bedtime',
        targetTime: '07:00',
        sleepType: 'normal',
        fallAsleepTime: 15,
        wakeUpBuffer: 10,
        napDuration: 20,
        sleepEfficiency: 0.85,
        circadianType: 'normal',
        weekdayVsWeekend: false,
        result: null,
        isCalculating: false,

        // Actions
        setMode: (mode) => set({ mode, result: null }),
        setTargetTime: (targetTime) => set({ targetTime, result: null }),
        setSleepType: (sleepType) => set({ sleepType, result: null }),
        setFallAsleepTime: (fallAsleepTime) => set({ fallAsleepTime, result: null }),
        setWakeUpBuffer: (wakeUpBuffer) => set({ wakeUpBuffer, result: null }),
        setCustomCycleDuration: (customCycleDuration) => set({ customCycleDuration, result: null }),
        setNapDuration: (napDuration) => set({ napDuration, result: null }),
        setSleepEfficiency: (sleepEfficiency) => set({ sleepEfficiency, result: null }),
        setCircadianType: (circadianType) => set({ circadianType, result: null }),
        setWeekdayVsWeekend: (weekdayVsWeekend) => set({ weekdayVsWeekend, result: null }),
        
        calculateSleep: async () => {
          set({ isCalculating: true });
          
          // Simulate async calculation
          await new Promise(resolve => setTimeout(resolve, 500));
          
          const state = get();
          
          // Use the enhanced calculation function
          try {
            const calculationResult = calculateOptimalTimes(
              state.mode,
              state.targetTime,
              state.sleepType,
              state.fallAsleepTime,
              state.wakeUpBuffer,
              state.napDuration,
              state.circadianType,
              state.sleepEfficiency,
              state.weekdayVsWeekend
            );
            
            // Transform to match SleepResult interface
            const result: SleepResult = {
              recommendedTimes: calculationResult.recommendedTimes.map((r: any) => ({
                hours: r.time?.hours || r.hours,
                minutes: r.time?.minutes || r.minutes
              })),
              selectedTime: calculationResult.recommendedTimes[0]?.time || calculationResult.recommendedTimes[0] || { hours: 22, minutes: 0 },
              cycleCount: calculationResult.recommendedTimes[0]?.cycles || 5,
              totalSleepDuration: calculationResult.recommendedTimes[0]?.duration || 450,
              sleepQuality: calculationResult.recommendedTimes[0]?.quality || 'good',
              tips: calculationResult.tips || []
            };
            
            set({ result, isCalculating: false });
          } catch (error) {
            console.error('Sleep calculation error:', error);
            // Fallback to old calculation
            const result = calculateSleepCycles(
              state.targetTime,
              state.mode,
              state.sleepType,
              state.fallAsleepTime
            );
            set({ result, isCalculating: false });
          }
        },
        
        clearResult: () => set({ result: null }),
        reset: () => set({
          mode: 'bedtime',
          targetTime: '07:00',
          sleepType: 'normal',
          fallAsleepTime: 15,
          wakeUpBuffer: 10,
          result: null,
          isCalculating: false,
          customCycleDuration: undefined
        })
      }),
      {
        name: 'sleepcalc-calculator',
        partialize: (state) => ({
          mode: state.mode,
          targetTime: state.targetTime,
          sleepType: state.sleepType,
          fallAsleepTime: state.fallAsleepTime,
          wakeUpBuffer: state.wakeUpBuffer,
          customCycleDuration: state.customCycleDuration,
          sleepEfficiency: state.sleepEfficiency,
          circadianType: state.circadianType,
          weekdayVsWeekend: state.weekdayVsWeekend
        })
      }
    )
  )
);

export const useUIStore = create<UIStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        activeSection: 'calculator',
        isMobileMenuOpen: false,
        theme: 'auto',
        notifications: [],

        // Actions
        setActiveSection: (activeSection) => set({ activeSection, isMobileMenuOpen: false }),
        toggleMobileMenu: () => set((state) => ({ isMobileMenuOpen: !state.isMobileMenuOpen })),
        setTheme: (theme) => set({ theme }),
        
        addNotification: (notification) => {
          const id = Math.random().toString(36).substr(2, 9);
          const newNotification = { ...notification, id };
          
          set((state) => ({
            notifications: [...state.notifications, newNotification]
          }));
          
          // Auto-remove notification after duration
          if (notification.duration) {
            setTimeout(() => {
              get().removeNotification(id);
            }, notification.duration);
          }
        },
        
        removeNotification: (id) => set((state) => ({
          notifications: state.notifications.filter(n => n.id !== id)
        })),
        
        clearNotifications: () => set({ notifications: [] })
      }),
      {
        name: 'sleepcalc-ui',
        partialize: (state) => ({
          activeSection: state.activeSection,
          theme: state.theme
        })
      }
    )
  )
);

export const useScenariosStore = create<ScenariosStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        selectedScenario: null,
        customScenarios: [],

        // Actions
        selectScenario: (scenario) => set({ selectedScenario: scenario }),
        
        applyScenarioToCalculator: (scenario) => {
          const calculatorStore = useCalculatorStore.getState();
          
          // Apply scenario-specific settings to calculator
          const scenarioConfigs = {
            student: { sleepType: 'normal' as SleepType, targetTime: '07:00', fallAsleepTime: 20 },
            worker: { sleepType: 'normal' as SleepType, targetTime: '06:30', fallAsleepTime: 15 },
            parent: { sleepType: 'fast' as SleepType, targetTime: '06:00', fallAsleepTime: 10 },
            athlete: { sleepType: 'slow' as SleepType, targetTime: '06:00', fallAsleepTime: 10 },
            shift: { sleepType: 'normal' as SleepType, targetTime: '14:00', fallAsleepTime: 25 },
            senior: { sleepType: 'slow' as SleepType, targetTime: '06:00', fallAsleepTime: 30 },
            teen: { sleepType: 'slow' as SleepType, targetTime: '07:30', fallAsleepTime: 25 },
            child: { sleepType: 'fast' as SleepType, targetTime: '07:00', fallAsleepTime: 15 }
          };
          
          const config = scenarioConfigs[scenario];
          if (config) {
            calculatorStore.setSleepType(config.sleepType);
            calculatorStore.setTargetTime(config.targetTime);
            calculatorStore.setFallAsleepTime(config.fallAsleepTime);
          }
          
          set({ selectedScenario: scenario });
        },
        
        clearScenario: () => set({ selectedScenario: null })
      }),
      {
        name: 'sleepcalc-scenarios'
      }
    )
  )
);

export const useTimeWheelStore = create<TimeWheelStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      currentTime: new Date(),
      moonPhase: calculateMoonPhase(new Date()),
      showLabels: true,
      hourFormat: 12,

      // Actions
      updateCurrentTime: () => {
        const currentTime = new Date();
        const moonPhase = calculateMoonPhase(currentTime);
        set({ currentTime, moonPhase });
      },
      
      setHourFormat: (hourFormat) => set({ hourFormat }),
      toggleLabels: () => set((state) => ({ showLabels: !state.showLabels })),
      updateMoonPhase: () => {
        const moonPhase = calculateMoonPhase(get().currentTime);
        set({ moonPhase });
      }
    })
  )
);

export const useUserStore = create<UserStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state - Free user (default)
        profile: null,
        isLoggedIn: false,
        subscription: null,
        isProUser: false,
        isPremiumUser: false,

        // Actions
        setProfile: (profile) => set({ profile, isLoggedIn: true }),
        updateProfile: (updates) => set((state) => ({
          profile: state.profile ? { ...state.profile, ...updates } : null
        })),
        clearProfile: () => set({ 
          profile: null, 
          isLoggedIn: false,
          subscription: null,
          isProUser: false,
          isPremiumUser: false
        }),
        login: () => set({ isLoggedIn: true }),
        logout: () => set({ 
          profile: null, 
          isLoggedIn: false,
          subscription: null,
          isProUser: false,
          isPremiumUser: false
        }),

        // Subscription actions
        setSubscription: (subscription) => {
          const isProUser = subscription.plan === 'pro' && subscription.status === 'active';
          const isPremiumUser = subscription.plan === 'premium' && subscription.status === 'active';
          
          set({ 
            subscription, 
            isProUser, 
            isPremiumUser 
          });
        },
        
        updateSubscription: (updates) => set((state) => {
          if (!state.subscription) return state;
          
          const updatedSubscription = { ...state.subscription, ...updates };
          const isProUser = updatedSubscription.plan === 'pro' && updatedSubscription.status === 'active';
          const isPremiumUser = updatedSubscription.plan === 'premium' && updatedSubscription.status === 'active';
          
          return {
            subscription: updatedSubscription,
            isProUser,
            isPremiumUser
          };
        }),
        
        cancelSubscription: () => set((state) => {
          if (!state.subscription) return state;
          
          return {
            subscription: {
              ...state.subscription,
              status: 'cancelled' as SubscriptionStatus,
              autoRenew: false
            },
            isProUser: false,
            isPremiumUser: false
          };
        }),
        
        hasProAccess: () => {
          const state = get();
          return state.subscription?.plan === 'pro' && state.subscription?.status === 'active';
        },
        
        hasPremiumAccess: () => {
          const state = get();
          return state.subscription?.plan === 'premium' && state.subscription?.status === 'active';
        },
        
        getSubscriptionStatus: () => {
          const state = get();
          return state.subscription?.status || null;
        },
        
        isSubscriptionActive: () => {
          const state = get();
          return state.subscription?.status === 'active';
        },
        
        isFeatureAvailable: (featureTier: 'pro' | 'premium') => {
          const state = get();
          
          if (!state.subscription || state.subscription.status !== 'active') {
            return false;
          }
          
          if (featureTier === 'pro') {
            return state.subscription.plan === 'pro' || state.subscription.plan === 'premium';
          }
          
          if (featureTier === 'premium') {
            return state.subscription.plan === 'premium';
          }
          
          return false;
        }
      }),
      {
        name: 'sleepcalc-user',
        partialize: (state) => ({
          profile: state.profile,
          isLoggedIn: state.isLoggedIn,
          subscription: state.subscription,
          isProUser: state.isProUser,
          isPremiumUser: state.isPremiumUser
        })
      }
    )
  )
);